import React, {
    useCallback,
    useEffect,
    useEffectEvent,
    useMemo,
    useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Task } from "@common/types/task";
import { CounterpartyDirectoryViewSchema } from "@common/types/views/counterpartyDirectoryView";
import { counterpartiesQueryKeys } from "@api/counterparty.api";
import { DirectoryFilters } from "@components/settings/Directory/lib";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { StickyTaskMembershipPending } from "../shared/StickyTaskMembershipPending";
import { useStickyRefreshOnMount } from "../shared/useStickyRefreshOnMount";
import { useTaskTableViewState } from "../shared/useTaskTableViewState";
import { CounterpartiesTaskTable } from "./CounterpartiesTaskTable";
import {
    PINNED_COUNTERPARTIES_FILTER_TABS,
    WORKSPACE_COUNTERPARTIES_FILTER_TABS,
} from "./counterpartiesTaskFilters";
import {
    parseCounterpartiesTaskView,
    toDirectoryFilters,
} from "./counterpartiesTaskView";
import styles from "./Counterparties.module.scss";

interface Props {
    selectedTask: Task;
}

export const CounterpartiesTaskBody: React.FC<Props> = ({ selectedTask }) => {
    const queryClient = useQueryClient();
    const {
        taskView,
        taskViewKey,
        sizedView,
        allowedColumnIds,
        handleViewChange,
        handleFiltersChange,
    } = useTaskTableViewState({
        selectedTask,
        viewSchema: CounterpartyDirectoryViewSchema,
        parseView: parseCounterpartiesTaskView,
    });
    const originalFilters = selectedTask.view?.originalFilters ?? undefined;
    // Sticky tasks keep period and entity on live filters and criteria on
    // originalFilters; prefer the latter for the date scope when present.
    const scopeSource = originalFilters ?? taskView.filters;

    const [scope, setScope] = useState<DirectoryFilters>(() =>
        toDirectoryFilters(scopeSource),
    );

    const resetScope = useEffectEvent(() => {
        setScope(toDirectoryFilters(scopeSource));
    });
    // Re-sync only when the task or its view content changes.
    useEffect(() => {
        resetScope();
    }, [selectedTask.id, taskViewKey]);

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: counterpartiesQueryKeys.query(selectedTask.workspaceId),
        });
    }, [queryClient, selectedTask.workspaceId]);

    const { isMembershipPending, resolvedMembership } = useStickyRefreshOnMount(
        { selectedTask, onMembershipAdded: invalidateList },
    );
    const stickyCounterpartiesIds =
        resolvedMembership?.counterpartiesIds ??
        selectedTask.view?.filters?.counterpartiesIds;

    const hasPinnedIds = !!taskView.filters.counterpartiesIds?.length;
    const availableFilters = useMemo(() => {
        if (originalFilters) {
            return [];
        }
        return hasPinnedIds
            ? PINNED_COUNTERPARTIES_FILTER_TABS
            : WORKSPACE_COUNTERPARTIES_FILTER_TABS;
    }, [originalFilters, hasPinnedIds]);

    const isScopeLocked = !!originalFilters || hasPinnedIds;

    if (isMembershipPending) {
        return <StickyTaskMembershipPending />;
    }

    return (
        <div className={styles.root}>
            <CounterpartiesTaskTable
                queryScopeKey={selectedTask.id}
                view={sizedView}
                onViewChange={handleViewChange}
                onFiltersChange={handleFiltersChange}
                scope={scope}
                onScopeChange={setScope}
                availableFilters={availableFilters}
                defaultView={taskView}
                allowedColumnIds={allowedColumnIds}
                lockScope={isScopeLocked}
                originalFilters={originalFilters}
                stickyCounterpartiesIds={stickyCounterpartiesIds}
                className={styles.filters}
                tableClassName={styles.table}
                emptyView={() => (
                    <EmptyState
                        header={
                            originalFilters ||
                            taskView.filters.counterpartiesIds
                                ? "No counterparties associated with this task"
                                : "No counterparties matching the task definition"
                        }
                    />
                )}
            />
        </div>
    );
};
